<template>
  <FilterNoResults
    v-if="showNoResultsNotice"
    @on-close="showNoResultsNotice = false"
  />
  <!-- the sticky filters bar's containing block: it ends with the results and
       pagination, so the bar scrolls away with the last results instead of
       staying stuck over the rest of the page (city content, reviews, …) -->
  <div class="filters-scope">
    <!-- Sticky + breakout on same wrapper: sticky must not share a node with overflow-hidden (breaks WebKit/Chromium). -->
    <div
      ref="filtersBar"
      class="filters-bar -mx-4 md:mx-0 sticky top-[110px] sm:top-[69px] md:top-[--kft-navbar-height] bg-white sm:block"
      :class="[zIndex, { 'filters-bar--raised': hasResultsBehindFiltersBar }]"
    >
      <ClientOnly>
        <template #fallback>
          <div class="px-4 md:px-0">
            <FilterBarSkeleton />
          </div>
        </template>
        <div class="px-4 md:px-0">
          <div class="filter-wrap">
            <Filters
              :facets="filterOptions"
              :filters="filters"
              @filters-updated="handleFiltersUpdate"
            />

            <FiltersSelected
              :location-label="currentLocality?.name"
              @filters-updated="handleFiltersUpdate"
            />
          </div>
          <div class="flex justify-between mt-0 pb-2">
            <FilterResultCount
              class="text-xs text-gray-500"
              :hits-length="searchResults?.nbHits"
              :filters="{
                ...filters,
                locality: {
                  name: filters.location,
                },
                categories: filters.categories,
              }"
              :current-locality="currentLocality?.name"
              :is-no-results="showNoResultsNotice"
            />
            <FilterOrderBy />
          </div>
        </div>
      </ClientOnly>
    </div>
    <!-- :query-id="topEventsQueryID" -->
    <!-- plain wrapper so the results can be measured: ProductShowcase has a
         leading comment, so in dev its root is a fragment, not an element -->
    <div ref="results" class="mt-4">
      <ProductShowcase
        id="filteredProductShowCasePageTop"
        :algolia-insights-client="algoliaInsightsClient"
        data-test-id="top-events-section"
        :products="searchResults?.hits || []"
        image-loading="lazy"
        :tags="[]"
        :loading="loading"
        :algolia-insights-label="algoliaEventName"
        :gtm-item-list-id="gtmItemListId"
        :gtm-search-term="gtmSearchTerm"
        :use-h-tag="false"
        :query-id="algoliaQueryId"
        :showcase-container-data-test-id="'filtered-product-showcase-results'"
        :special-giftcard-event="specialGiftcardEvents[0]"
        :card-context="getProductCardContext(pageType)"
      >
        <template #promotion1="{ isProductCardV2 }">
          <PromoProductCard
            :layout="isProductCardV2 ? 'v2' : 'default'"
            color="red"
            :icon="Gift"
            :badge-label="t('general.promotionCards.giftcard.badge')"
            :title="t('general.promotionCards.giftcard.title')"
            :subtitle="t('general.promotionCards.giftcard.subtitle')"
            :cta-label="t('general.promotionCards.giftcard.CTALabel')"
            :link="localePath('/giftcard')"
            image="/_nuxt3-static/promo-cards/giftbox.webp"
          />
        </template>
        <template #promotion2="{ isProductCardV2 }">
          <PromoProductCard
            :layout="isProductCardV2 ? 'v2' : 'default'"
            color="teal"
            :icon="MessageCircleCheck"
            :badge-label="t('general.promotionCards.teamevents.badge')"
            :title="t('general.promotionCards.teamevents.title')"
            :subtitle="t('general.promotionCards.teamevents.subtitle')"
            :cta-label="t('general.promotionCards.teamevents.CTALabel')"
            image="/_nuxt3-static/promo-cards/teamevent-card.jpeg"
            @click="toggleDateRequestSidebar(route.name, 'search_results')"
          />
        </template>
      </ProductShowcase>
    </div>
    <FilterPagination
      :hits-per-page="searchResults?.hitsPerPage ?? 0"
      :nb-hits="searchResults?.nbHits ?? 0"
      :nb-pages="searchResults?.nbPages"
      @updated="handleChangePage"
    />
  </div>
</template>
<script setup lang="ts">
import type { Hit } from '@nuxtjs/algolia'
import { useEventListener, useResizeObserver } from '@vueuse/core'
import { useRouteParams, useRouteQuery } from '@vueuse/router'
import isEqual from 'lodash/isEqual'
import cloneDeep from 'lodash/cloneDeep'
import useSearchEngineEventSchema from '~/composables/schemaOrg/useSearchEngineEventSchema'
import useTransactionalPageHead from '~/composables/transactionalPage/useTransactionalPageHead'
import {
  useTransactionalPageSearch,
  useTransactionalPageSearchSideEffects,
} from '~/composables/useTransactionalPageSearch'
import type { Category } from '~/composables/types/event'
import {
  EventListPageType,
  type StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'

import type {
  SearchEngineLocation,
  SearchEngineEventItem,
} from '~/composables/types/searchEngine'
import {
  buildAlgoliaAnalyticsTags,
  buildTransactionalSearchKey,
  getAlgoliaSearchOptions,
  getListPageFederatedAnalyticsTags,
  getTypesenseSearchOptions,
} from '~/utils/searchEngineHelpers'
import { useSearchEngine } from '~/composables/search/useSearchEngine'
import { useSearchIndex } from '~/composables/search/useSearchIndex'
import {
  useTypesenseGeoSort,
  refetchOnceGeoResolved,
} from '~/composables/useTypesenseGeoSort'
import { consumeFederatedSearchAnalyticsOutcome } from '~/utils/federatedSearchAnalyticsSession'
import type { SearchFilterOptions } from '~/composables/useSearchFilters'
import { useTransactionalProductSchema } from '~/composables/schemaOrg/useTransactionalProductSchema'
import useSearchEngineEventCarouselSchema from '~/composables/schemaOrg/useSearchEngineEventCarouselSchema'
import { eventListPageCategoryUrl } from '~/components/transactionalPage/requestParams'
import { useAlgoliaSearchInsights } from '~/composables/algolia/useAlgoliaSearchInsights'
import { useAlgoliaIndexNames } from '~/composables/algolia/useAlgoliaIndexNames'
import { getLocationDisplayName } from '~/components/FederatedSearch/helpers'
import { HITS_PER_PAGINATION } from '~/composables/algolia/constants'
import { sendFilterSelected, sendSearch } from '~/composables/useGtmEvents'
import { getGtmContentGroup } from '~/utils/getGtmContentGroup'
import { getListingGtmItemListId } from '~/utils/getListingGtmItemListId'
import {
  localitySlugMatchesFilter,
  localitySlugForAlgoliaOptionalFilter,
} from '~/utils/localitySlugMatch'
import {
  getAlgoliaLocalityRankingRawOptions,
  getTypesenseSortParams,
} from '~/utils/searchEngineLocalityGeoFallback'
import { useTransactionalFilterNavigationWatch } from '~/composables/transactionalPage/useTransactionalFilterNavigationWatch'
import { runTransactionalSearchIfNeeded } from '~/composables/transactionalPage/useTransactionalSearch'
import { buildCategoryNameToSlugMapFromSearchEngineHits } from '~/utils/buildCategoryNameToSlugMapFromSearchEngineHits'
import { TYPO_PROFILES } from '~/utils/typesenseTypoTolerance'
import {
  EVENTS_QUERY_BY,
  buildEventsTextSearchParams,
} from '~/utils/typesenseEventsTextSearch'
import { getProductCardContext } from '~/utils/productCardMapper'
import { Gift, MessageCircleCheck } from 'lucide-vue-next'
const route = useRoute()
const router = useRouter()
const gtm = useKftGtm()
const {
  public: { baseUrl, appEnv },
} = useRuntimeConfig()
const locationSlugParam = useRouteParams('location')
const locationSlugQuery = useRouteQuery('location')
const categorySlugParam = useRouteParams('category')
const tagSlugParam = useRouteParams('slug')

/** Consumed once on client when landing from federated search (sessionStorage). */
const federatedSearchOutcome = ref<string | null>(null)
if (import.meta.client) {
  federatedSearchOutcome.value = consumeFederatedSearchAnalyticsOutcome()
}
const { toggleDateRequestSidebar } = useUiState()
const { locale, t } = useI18n()
const localePath = useLocalePath()

const showNoResultsNotice = ref(false)
/** After zero hits with locality optional filters, next search uses IP geo ranking. */
const isGeoFallbackAfterEmptyLocalityHits = ref(false)
const keepNoResultsNoticeOnNextFilterSearch = ref(false)
provide('noResultsVisible', showNoResultsNotice)

const locationSlug = computed(() =>
  (locationSlugQuery.value || locationSlugParam.value)?.toString()
)

const categorySlug = computed(() => categorySlugParam.value?.toString())

const props = withDefaults(
  defineProps<{
    pageType: EventListPageType
    eventListPageData?: StrapiEventListPage
    loadSchema?: boolean
    reviewsData?: {
      reviews: any[]
      averageRating: string
      reviewCount: number
    } | null
  }>(),
  {
    eventListPageData: undefined,
    loadSchema: false,
    reviewsData: null,
  }
)

const { client: algoliaInsightsClient } = useAlgoliaSearchInsights()

const PAGE_KEY = route.path

// Generate human-readable Algolia event name based on page type
const algoliaEventName = computed(() => {
  switch (props.pageType) {
    case EventListPageType.SEARCH:
      return 'Search Page'
    case EventListPageType.CITY:
      return 'City Page'
    case EventListPageType.CATEGORY:
      return 'Category Page'
    case EventListPageType.CITY_CATEGORY:
      return 'City Category Page'
    case EventListPageType.TEAMEVENT:
      return 'Teamevent Page'
    case EventListPageType.KITS:
      return 'Kits Page'
    case EventListPageType.GENERAL:
    default:
      return 'General Page'
  }
})

const gtmItemListId = computed(() => getListingGtmItemListId(props.pageType))

const eventListPageAttributes = computed(() => {
  return props.eventListPageData
})
const { locations: locationsIndex } = useAlgoliaIndexNames()

const { result: locationsAlgoliaRaw } =
  useAlgoliaIndexSearch<SearchEngineLocation>(locationsIndex)

const searchEngineLocalities = computed(() => {
  const raw = locationsAlgoliaRaw.value
  if (!raw) return null
  return {
    ...raw,
    hits: (raw.hits ?? []).map((h) => ({
      ...h,
      name: getLocationDisplayName(h, locale.value),
    })),
  }
})

provide('searchEngineLocalities', searchEngineLocalities)

const { data: categoryResults, execute: searchCategories } =
  await useKftApiFetch<{
    data: Category
  }>(eventListPageCategoryUrl({ categorySlug: categorySlug.value }))

const initialCategoryName = computed(() => categoryResults.value?.data?.name)

const indexName =
  props.pageType === EventListPageType.SEARCH
    ? getFederatedSearchIndexName(locale.value)
    : getEventListIndexName(locale.value)

const defaultFilters = computed<Partial<SearchFilterOptions>>(() => ({
  ...(initialCategoryName.value && { categories: [initialCategoryName.value] }),
  // Only use the route path param here (city slug), NOT the query param.
  // If we included the query param, `isDefaultFilter` would treat any user-selected
  // location as a "default" and strip it from the URL when other filters change.
  location: locationSlugParam.value?.toString(),
}))

const { filters } = useSearchFilters(PAGE_KEY, defaultFilters)
provide('productsFilters', filters)

const { filteredGiftcardEvents: specialGiftcardEvents } =
  await useGiftcardEvents(filters)

const currentLocality = computed(
  () =>
    searchEngineLocalities.value?.hits?.find((d) =>
      localitySlugMatchesFilter(
        d.slug,
        locationSlug.value || filters.value.location
      )
    ) as SearchEngineLocation
)

// Keep default Strapi tags in sync when navigating between transactional pages
// (e.g. teamevent A → teamevent B). A one-time init would leave the previous
// page's tags in `filters` when the new page has none.
watch(
  () => props.eventListPageData?.searchFilters?.tags,
  (tags) => {
    filters.value.tags = tags?.length ? tags.map((d) => d.label) : []
  },
  { immediate: true }
)

const { currentOrderBy, currentPage } = useLocalizedOrderBy()
const hitsPerPagination = ref(HITS_PER_PAGINATION)

const fetchLocalitiesAndCategories = async (
  value: Partial<SearchFilterOptions>,
  oldValue: Partial<SearchFilterOptions> | undefined
) => {
  // Only re-fetch when data is absent or categories have explicitly changed.
  // Skips the redundant execute() call on the initial immediate watcher trigger
  // when the payload is already hydrated from SSR.
  const categoriesChanged =
    oldValue !== undefined && !isEqual(value?.categories, oldValue?.categories)

  if (!categoryResults.value || categoriesChanged) {
    await searchCategories()
  }
}

// This updates the amount of hits per pagination
// based on the amount of special giftcard events
// to keep a consistent amount of hits per pagination
const updateAmountOfHitsPerPagination = () => {
  if (specialGiftcardEvents.value?.length > 0) {
    hitsPerPagination.value = HITS_PER_PAGINATION - 1
  } else {
    hitsPerPagination.value = HITS_PER_PAGINATION
  }
}

updateAmountOfHitsPerPagination()

// KFT-4028: typesense has no replica indices, so its "index" is always the
// base collection — sort flows via a query-time sort_by instead (below).
// Reactive (KFT-4115): an A/B force can switch it after hydration, which
// rebuilds the search instance and refetches (see the side effects below).
const searchEngine = useSearchEngine()
// The engine the client resolved at boot (A/B force cookie / ?searchEngine=),
// already final during hydration (KFT-4167).
const clientSearchEngine = useSearchEngine({ clientOnly: true })
// Algolia's client-side geo re-search (aroundLatLngViaIP; the SSR payload is
// geo-free) only when Algolia is both the hydration and the client-resolved
// engine. After an A/B force either way, the engine switch refetches on the
// target engine, so it would only hit the engine being left (KFT-4167).
const isAlgoliaGeoReSearch = computed(
  () =>
    import.meta.client &&
    searchEngine.value === 'algolia' &&
    clientSearchEngine.value === 'algolia'
)

// KFT-4116: resolve the viewer's coordinates for Typesense proximity ranking,
// replacing Algolia's aroundLatLngViaIP. Client-only and Typesense-only —
// `geoCoords` stays null until (and unless) /api/geo resolves, so the first
// render is geo-free and the list reorders once coordinates arrive (see
// `refetchOnceGeoResolved` below — chained off this promise directly rather
// than a reactive watcher, which would race with the first search). The
// Algolia path keeps aroundLatLngViaIP and skips this fetch entirely.
const { coords: geoCoords, resolveGeoCoords } = useTypesenseGeoSort()
// Started for the initial engine only (SEARCH_ENGINE=typesense); a switch to
// Typesense after hydration (KFT-4115 A/B force) resolves geo in the engine
// watcher below, once the search can be refetched.
const geoCoordsResolved =
  import.meta.client && searchEngine.value === 'typesense'
    ? resolveGeoCoords()
    : null

const currentIndexName = computed(() =>
  searchEngine.value === 'algolia'
    ? currentOrderBy.value?.indexName || indexName
    : indexName
)

// Shared between the algolia and typesense locality-ranking translations —
// see getAlgoliaLocalityRankingRawOptions / getTypesenseLocalityRankingParams.
const localityRankingInput = computed(() => {
  const locationFilter = filters.value?.location
  const locationType = filters.value?.locationType
  return {
    hasLocationOptionalFilter: Boolean(locationFilter),
    onlineFilterActive: filters.value?.typeOfClass === 'online',
    isGeoFallbackAfterEmptyLocalityHits:
      isGeoFallbackAfterEmptyLocalityHits.value,
    useRegionsOptionalFilter:
      locationType === 'region' || Boolean(locationFilter?.includes('--')),
    slugForOptionalFilter: localitySlugForAlgoliaOptionalFilter(
      locationFilter ?? ''
    ),
    // Algolia-only; kept false on Typesense (which ignores it) so its SSR and
    // client search keys match and the SSR payload hydrates without a refetch.
    enableAroundLatLngViaIP: isAlgoliaGeoReSearch.value,
    // Typesense-only proximity coords (null on the Algolia path / before geo
    // resolves); consumed by getTypesenseSortParams, ignored by the Algolia one.
    coords: geoCoords.value,
    hasQuery: Boolean(filters.value?.query?.toString().trim()),
  }
})

// Shared with the federated search (KFT-4145): the same query returns the
// same events in the same order in the dropdown and here.
const EVENT_QUERY_BY = EVENTS_QUERY_BY

// Relevancy gets its own richer tiered sort_by (city + availability, then
// trending/promotions as tiebreakers). Newest/price-asc keep the simpler
// locality boost (soft ranking, mirrors Algolia's optionalFilters weighting)
// ahead of the user's explicit sort field, or hand off to their preset when
// there's no boost to prepend — see getTypesenseSortParams.
const typesenseSort = computed(() =>
  getTypesenseSortParams(currentOrderBy.value, localityRankingInput.value)
)

// Algolia serves newest / price-asc from sort replicas with looser typo
// tolerance than the primary index behind relevancy; mirror that per sort.
const SORT_REPLICA_SLUGS = ['newest', 'price-asc']

const toTypesenseEventParams = () => ({
  ...getTypesenseSearchOptions({
    filters: filters.value,
    rawOptions: {
      hitsPerPage:
        HITS_PER_PAGINATION - (specialGiftcardEvents.value.length > 0 ? 1 : 0),
      page: currentPage.value - 1,
      sortBy: typesenseSort.value.sortBy,
      preset: typesenseSort.value.preset,
    },
  }),
  // Shared events text search (KFT-4145): q, fields, typo + text-match
  // settings and stopwords, exactly as the federated search sends them.
  ...buildEventsTextSearchParams({
    text: filters.value?.query?.toString() ?? '',
    collection: currentIndexName.value,
    typoProfile: SORT_REPLICA_SLUGS.includes(currentOrderBy.value?.slug ?? '')
      ? TYPO_PROFILES.eventSortReplicas
      : TYPO_PROFILES.events,
  }),
})

const createEventSearchInstance = (name: string) =>
  useSearchIndex<SearchEngineEventItem>(name, {
    typesenseQueryBy: EVENT_QUERY_BY,
    toTypesenseParams: toTypesenseEventParams,
    // City, category, tag and teamevent pages repeat the same few requests
    // across viewers; /search is per query, so it skips the cache.
    typesenseCache: props.pageType !== EventListPageType.SEARCH,
  })

const algoliaSearch = ref(createEventSearchInstance(currentIndexName.value))

const listPageAnalyticsTags = computed(() =>
  buildAlgoliaAnalyticsTags(
    appEnv,
    getListPageFederatedAnalyticsTags(federatedSearchOutcome.value)
  )
)

const algoliaFilterParams = computed(() => {
  return getAlgoliaSearchOptions({
    filters: filters.value,
    rawOptions: {
      hitsPerPage:
        HITS_PER_PAGINATION - (specialGiftcardEvents.value.length > 0 ? 1 : 0),
      page: currentPage.value - 1,
      analyticsTags: listPageAnalyticsTags.value,
      /**
       * if online filter is active we need to show only online events
       * and ignore the locality
       */
      ...getAlgoliaLocalityRankingRawOptions(localityRankingInput.value),
    },
  })
})

const {
  data: searchResults,
  status,
  execute: performSearch,
  error: searchError,
} = await useTransactionalPageSearch(
  algoliaSearch,
  currentIndexName,
  algoliaFilterParams,
  hitsPerPagination
)

// Serialized signature of the effective Algolia query. Mirrors the key
// `useTransactionalPageSearch` builds internally (same helper) so the
// watcher below can dedupe searches whose query hasn't actually changed.
const queryParam = useRouteQuery('query')
// Only the search page attributes clicks to a search term
const gtmSearchTerm = computed(() =>
  props.pageType === EventListPageType.SEARCH
    ? queryParam.value?.toString().trim() || undefined
    : undefined
)
const searchKey = computed(() =>
  buildTransactionalSearchKey({
    indexName: currentIndexName.value,
    searchQuery: queryParam.value?.toString(),
    options: algoliaFilterParams.value,
  })
)

const loading = computed(() => status.value === 'pending')

useTransactionalPageSearchSideEffects(
  currentIndexName,
  loading,
  algoliaSearch,
  performSearch,
  searchError,
  createEventSearchInstance,
  // Algolia sorts via currentIndexName (replica indices) already — only
  // typesense needs this extra trigger (sort is a query-time param on one
  // collection, so currentIndexName never changes when only the sort
  // changes). Deliberately just the sort slug, not typesenseSort as a
  // whole — that computed also includes the locality boost, which already
  // changes (and refetches) via the `filters` watcher whenever location/
  // online change; using it here would double-fire on those changes.
  // Geo (KFT-4116) is handled separately below via `refetchOnceGeoResolved`,
  // not folded in here — a `watch()`-based trigger races with the initial
  // search (see the comment above `geoCoordsResolved`).
  computed(() =>
    searchEngine.value === 'algolia' ? '' : (currentOrderBy.value?.slug ?? '')
  ),
  searchEngine
)

// KFT-4116: apply geo with exactly one follow-up search once resolution
// settles, whether that happens before or after the watcher above is wired
// up — see the comment above `geoCoordsResolved`.
if (geoCoordsResolved) {
  refetchOnceGeoResolved(geoCoordsResolved, performSearch)
}

// KFT-4115: switched to Typesense after hydration (A/B force). The side
// effects above already refetch on the switch — picking up coords if geo has
// resolved before — so only chain a geo refetch when coords are still missing.
watch(searchEngine, (engine) => {
  if (import.meta.client && engine === 'typesense' && !geoCoords.value) {
    refetchOnceGeoResolved(resolveGeoCoords(), performSearch)
  }
})

const algoliaQueryId = computed(() => searchResults.value?.queryID)

const filterOptions = computed(() => {
  return {
    ...searchResults.value?.facets,
    location: searchEngineLocalities.value?.hits,
  } as {
    [key: string]: any
    location: Array<Hit<SearchEngineLocation>>
  }
})

const categoryNameToSlug = computed(() =>
  buildCategoryNameToSlugMapFromSearchEngineHits(
    searchResults.value?.hits ?? []
  )
)

const isFilterOpen = ref(false)
provide('isFilterOpen', isFilterOpen)

const zIndex = computed(() => {
  return isFilterOpen.value ? 'z-20 md:z-10' : 'z-[9]'
})

// only draw the filters bar's bottom edge once results scroll underneath it.
// Both rects are read together so a layout change that moves one without
// resizing it can't leave a stale value behind.
const filtersBar = useTemplateRef<HTMLElement>('filtersBar')
const results = useTemplateRef<HTMLElement>('results')
const hasResultsBehindFiltersBar = ref(false)
const updateResultsBehindFiltersBar = () => {
  if (!filtersBar.value || !results.value) return
  hasResultsBehindFiltersBar.value =
    results.value.getBoundingClientRect().top <
    filtersBar.value.getBoundingClientRect().bottom
}
useEventListener(window, 'scroll', updateResultsBehindFiltersBar, {
  passive: true,
})
useResizeObserver([filtersBar, results], updateResultsBehindFiltersBar)
// the bar slides with the auto-hiding header without a scroll event
useEventListener(filtersBar, 'transitionend', updateResultsBehindFiltersBar)

const sendFilterSelectedEvent = (filters: SearchFilterOptions) => {
  // Transient: which interaction produced the date range (quick-select preset vs. calendar).
  const dateSource = filters.dateSource
  for (const [key, value] of Object.entries(filters)) {
    if (key === 'priceMin' || key === 'priceMax' || key === 'dateSource')
      continue

    if (!value || (Array.isArray(value) && value.length === 0)) continue

    const formatted = Array.isArray(value) ? value.join('; ') : value
    const eventValue =
      key === 'dates' && dateSource
        ? `${dateSource}: ${formatted}`
        : `${formatted}`
    sendFilterSelected(key, eventValue)
  }

  if (filters.priceMin && filters.priceMax) {
    sendFilterSelected('price', `${filters.priceMin}-${filters.priceMax}`)
  }
}

const handleFiltersUpdate = async (
  updatedFilters: SearchFilterOptions = {}
) => {
  sendFilterSelectedEvent(updatedFilters)

  // dateSource is analytics-only — strip it so it never lands in filters/URL.
  const persistable = { ...updatedFilters }
  delete persistable.dateSource

  filters.value = {
    ...filters.value,
    ...persistable,
  }

  const isDefaultFilter = (key: string, value: unknown): boolean => {
    const defaultValue =
      defaultFilters.value?.[key as keyof typeof defaultFilters.value]
    if (defaultValue === undefined) return false
    if (Array.isArray(defaultValue) && Array.isArray(value)) {
      return (
        defaultValue.length === value.length &&
        defaultValue.every((v, i) => v === (value as unknown[])[i])
      )
    }
    return defaultValue === value
  }

  // Delete UNDEFINED and FALSE values; skip filters that match route-param defaults.
  const cleanFilters = Object.entries(filters.value).reduce(
    (acc: Record<string, unknown>, [key, value]) => {
      if (isDefaultFilter(key, value)) return acc
      if (value !== undefined && value !== false) {
        acc[key] = value
      }
      return acc
    },
    {}
  )

  await router.push({
    query: {
      ...cleanFilters,
      // orderBy isn't part of `filters` (useLocalizedOrderBy reads it
      // straight off the route), so replacing the whole query with just
      // cleanFilters silently drops the user's chosen sort order.
      orderBy: route.query.orderBy,
      page: 1,
    },
  })
}

const handleChangePage = async (page: number) => {
  await router.push({ query: { ...router.currentRoute.value.query, page } })
  await performSearch()
}

watch(
  () => router.currentRoute.value.query.page,
  (newValue, oldValue) => {
    const currentRoute = router.currentRoute.value
    if (newValue !== oldValue) {
      const pageTitle =
        typeof document !== 'undefined' ? document?.title || '' : ''
      gtm.pushToDataLayer({
        routeName: currentRoute.name,
        content_group: getGtmContentGroup(currentRoute),
        pageType: 'PageView',
        pageUrl: baseUrl + currentRoute.fullPath,
        pagePath: currentRoute.fullPath,
        pageTitle,
        event: 'nuxtRoute',
      })
    }
  }
)
const computedHits = computed(() => searchResults.value?.hits || [])

const eventCount = computed(() => searchResults.value?.nbHits)

// GA4 `search` event for the /search results page. Fires once per distinct
// query term when its results resolve. `nbHits === 0` records a zero-result
// search. Only the SEARCH page type is a real user search — city/category
// listings share this component but must not emit search events.
let lastSentSearchTerm: string | null = null
watch(
  () => status.value,
  (newStatus, oldStatus) => {
    if (!import.meta.client) return
    if (props.pageType !== EventListPageType.SEARCH) return
    // Fire only on the transition INTO success, so `searchResults`/`nbHits`
    // belong to the query that just resolved. `searchKey` (and the route
    // `query`) change synchronously on a new search, but the fetch key is
    // static and `status`/`searchResults` only update once `performSearch`
    // completes — watching the key would emit the new term with the old count.
    if (newStatus !== 'success' || oldStatus === 'success') return

    // Dedupe on the term, not the full search key: pagination, filters and sort
    // change the key but not the query, and must not emit another `search`.
    const term = queryParam.value?.toString().trim()
    if (!term || term === lastSentSearchTerm) return

    lastSentSearchTerm = term
    sendSearch(term, searchResults.value?.nbHits ?? 0)
  },
  { immediate: true }
)

const searchHeading = computed(
  () =>
    eventListPageAttributes.value?.searchHeading ||
    eventListPageAttributes.value?.title ||
    ''
)

if (props.loadSchema) {
  const { script: eventSchemas } = useSearchEngineEventSchema(
    searchResults.value?.hits || []
  )

  const { script: eventCarouselSchemas } = useSearchEngineEventCarouselSchema(
    eventSchemas.value.innerHTML['@graph']
  )

  const { shouldAppendTransactionalProductSchema, schemaObj } =
    await useTransactionalProductSchema({
      locationSlug,
      tagSlug: computed(() => {
        if (props.pageType === EventListPageType.TEAMEVENT)
          return `${t('eventListPage.teamEventSlugPrefix')}-${locationSlug.value}`

        return tagSlugParam.value?.toString()
      }),
      categorySlug,
      eventListPageData: props.eventListPageData,
      pageType: props.pageType,
      searchHeading,
      eventCount,
      hits: computedHits,
      reviewsData: props.reviewsData,
    })

  useTransactionalPageHead(
    props.pageType,
    props.eventListPageData!,
    {
      script: [
        ...(searchResults.value?.hits.length > 0 ? [eventCarouselSchemas] : []),
        ...(shouldAppendTransactionalProductSchema.value && eventCount.value
          ? [schemaObj]
          : []),
      ],
    },
    {
      hits: computedHits,
    }
  )
}

// Needs to be called after the filters are updated
// so that the requests work synchronously if on a page that
// does not load on server side
//
// Details on disclaimer about "server: false":
// https://nuxt.com/docs/getting-started/data-fetching#return-values
watch(
  () => defaultFilters.value,
  async (value, oldValue) => {
    if (!import.meta.client) return // We want to avoid watchers to force refetches on SSR as they might be in vain
    // Only fetch category/locality metadata here. The search itself is driven
    // by the `filters` watcher below — and since `defaultFilters` feeds
    // `filters`, any change here propagates there — so calling `performSearch()`
    // again would just duplicate the search on mount.
    await fetchLocalitiesAndCategories(value, oldValue)
  },
  {
    immediate: true,
    deep: true,
  }
)

const updateFiltersStorage = () => {
  if (import.meta.client) {
    sessionStorage.setItem('filters', JSON.stringify(filters.value))
  }
}

// Tracks the last serialized query we actually searched, so `deep: true`
// re-runs of the `filters` watcher that don't change the effective Algolia
// query become no-ops instead of firing duplicate searches. Orchestration
// (guard + empty-results recovery) lives in `runTransactionalSearchIfNeeded`
// so it can be unit tested without mounting this component.
//
// Typesense: seeded to the current searchKey — useTransactionalPageSearch's
// useAsyncData already fetched this exact query, so the `immediate: true`
// watcher below would only fire a duplicate (geo arrives via
// `refetchOnceGeoResolved`). Algolia: seeded to null, so the client re-runs
// the search with aroundLatLngViaIP (the SSR payload is geo-free) — see
// `isAlgoliaGeoReSearch`.
const lastSearchKey = ref<string | null>(
  isAlgoliaGeoReSearch.value ? null : searchKey.value
)

const runTransactionalSearch = () =>
  runTransactionalSearchIfNeeded({
    isClient: import.meta.client,
    getSearchKey: () => searchKey.value,
    getLastSearchKey: () => lastSearchKey.value,
    commitSearchKey: (key) => {
      lastSearchKey.value = key
    },
    clearSearchKey: () => {
      lastSearchKey.value = null
    },
    keepNoResultsNoticeOnNextFilterSearch:
      keepNoResultsNoticeOnNextFilterSearch.value,
    setKeepNoResultsNotice: (value) => {
      keepNoResultsNoticeOnNextFilterSearch.value = value
    },
    setShowNoResultsNotice: (value) => {
      showNoResultsNotice.value = value
    },
    setGeoFallback: (value) => {
      isGeoFallbackAfterEmptyLocalityHits.value = value
    },
    updateAmountOfHitsPerPagination,
    updateFiltersStorage,
    performSearch,
    getHits: () => searchResults.value?.hits,
    hasError: () => Boolean(searchError.value),
    getFiltersMatchDefault: () => isEqual(filters.value, defaultFilters.value),
    resetFiltersToDefault: () => {
      filters.value = cloneDeep(defaultFilters.value)
    },
  })

watch(() => filters.value, runTransactionalSearch, {
  immediate: true,
  deep: true,
})

useTransactionalFilterNavigationWatch({
  pageType: () => props.pageType,
  filters,
  categoryNameToSlug,
  categorySlug,
})
</script>

<style lang="postcss" scoped>
.filter-wrap {
  @apply flex md:flex-row gap-2 md:gap-0 py-4 max-md:overflow-x-auto max-md:overflow-y-visible;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none; /* For Chrome, Safari, and Edge */
  }
}

/* stretch the sticky bar's containing block over the gap before the next
   section (ReviewsTransactional's mt-10), so the bar leaves together with
   that section's background instead of over a white strip. It must be
   content, not padding: sticky is bounded by the parent's content box. The
   negative margin cancels it out, so the page layout doesn't change */
.filters-scope {
  @apply -mb-10;

  &::after {
    content: '';
    @apply block h-10;
  }
}

/* follow the layout header when it auto-hides on scroll (useAutoHideHeader) */
.filters-bar {
  transition: top 0.3s ease;

  /* full-bleed background + bottom edge: the bar lives inside the page
     container, so without this, scrolled content shows beside it. body has
     overflow-x hidden, so 100vw doesn't add a horizontal scrollbar */
  &::before {
    content: '';
    @apply absolute inset-y-0 left-1/2 -z-10 w-screen -translate-x-1/2 bg-white border-b border-transparent;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;
  }

  &.filters-bar--raised::before {
    @apply border-gray-200;
    box-shadow: 0 4px 8px -4px rgb(0 0 0 / 0.08);
  }
}

:global(html.kft-header-hidden .filters-bar) {
  top: 0;
}
</style>
